import { useHelpfulLinks } from "js/placeholderData/helpfulLinks";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tab } from "@headlessui/react";
import { useRowPagination } from "hooks/useRowPagination";
import { stringFormatingToPascal } from "js/formatting";
import { useSelector } from "react-redux";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import config from "config/config";
import { getTypeOfPolicy } from "js/utilities";
import { useTranslation, Trans } from "react-i18next";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";
import moment from "moment";
import Carousel from "components/General/Carousel";
import HelpfulLinks from "components/HelpfulLinks";
import PolicyCard from "components/PolicyCard";
import SimpleDataRow from "components/General/SimpleDataRow";
import AnnuityPolicyCard from "components/AnnuityPolicyCard";
import LoadingOverlay from "react-loading-overlay";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import UserNotificationDialogOnLogin from "components/Profile/Dialogs/UserNotificationDialogOnLogin";
import CustomerRowPagination from "components/General/CustomerRowPagination";

export default function DeprecatedPolicies() {
  LoadingOverlay.propTypes = undefined;
  // setup breadcrumb
  useBreadcrumb([]);
  const { t } = useTranslation();
  const [policiesDisplay, setPoliciesDisplay] = useState("card");
  const { policies, claims, isLoading } = useSelector((state) => state.data);
  const helpfulLinks = useHelpfulLinks(policies);
  const { user } = useSelector((s) => s.user);
  const [firstName, setFirstName] = useState();
  const [openUserNotificationDialog, setOpenUserNotificationDialog] =
    useState(false);
  let viewServiceandClaim = useAuthorizationCheck(4);
  let viewThingsYouCanDo = useAuthorizationCheck(6);
  useEffect(() => {
    let party = policies[0]?.parties?.find(
      (x) => x.person?.taxId === user.identityNumber
    );
    setFirstName(party?.person?.firstName ?? "");
    if (
      config.E_DELIVERY_ENABLE === "true" &&
      user.notificationPreference === 0
    ) {
      setOpenUserNotificationDialog(true);
    }
  }, [policies, user]);

  const { rowsForDisplay: current, rowPaginationProps: paginationProps } =
    useRowPagination(policies, [3, 10, 25, "all"]);

  const showMyPolicies = policies
    .map((p) => p.annuityPayoutIndicatorFlag)
    .every((ele) => ele === "Y");

  return (
    <>
      {user?.userId && (
        <>
          <LoadingOverlay
            active={isLoading}
            spinner={
              <EverlakeLoadingSpinner
                loadingText={t("CustomerDashboard-loadingMessage")}
              />
            }
            styles={EverlakeLoadingOverlayStyle}
          >
            <UserNotificationDialogOnLogin
              showDialog={openUserNotificationDialog}
              setShowDialog={setOpenUserNotificationDialog}
            />
            <div className="container mb-8">
              <h1>
                {t("CustomerDashboard-welcomeMessage", {
                  name: firstName
                    ? stringFormatingToPascal(firstName)
                    : t("CustomerPolicies-Greating"),
                })}
              </h1>
              <hr />
            </div>

            <Carousel policies={policies} />

            <div className="container ui-component-spacing">
              <div className="ui-policy-grid">
                <div className="flex flex-row items-center justify-between p-4 md:p-0 lg:block">
                  <h2 className="text-xl md:text-3xl">
                    {t("CustomerDashboard-policyTitle")}
                  </h2>
                  <div className="hidden md:flex md:justify-between md:items-start">
                    <p className="hidden text-sm mr-6 lg:block">
                      <Trans
                        i18nKey="CustomerDashboard-policySubTitle"
                        values={{ date: moment().format("MM/DD/YYYY") }}
                      />
                    </p>
                  </div>
                </div>

                <hr className="hidden md:block" />

                {current?.length > 0 ? (
                  <>
                    <div
                      className={`${
                        policiesDisplay === "card"
                          ? "ui-grid-cards"
                          : "ui-grid-table"
                      }`}
                    >
                      {current.map((policy, index) => {
                        const policyType = getTypeOfPolicy(
                          policy?.product?.type
                        );
                        if (
                          config.ENABLE_ANNUITIES === "true" &&
                          policyType === "annuity"
                        ) {
                          return (
                            <AnnuityPolicyCard
                              data={policy}
                              key={`Annuity policy - ${index}`}
                            />
                          );
                        } else
                          return (
                            <PolicyCard
                              data={policy}
                              key={`policy - ${index}`}
                            />
                          );
                      })}
                    </div>

                    {/* <!-- paging component --> */}
                    <div className="flex justify-between items-start mt-6">
                      <div>
                        <p className="hidden text-sm md:block lg:hidden mr-6">
                          <Trans
                            i18nKey="CustomerDashboard-policySubTitle"
                            values={{ date: moment().format("MM/DD/YYYY") }}
                          />
                        </p>
                      </div>

                      <CustomerRowPagination {...paginationProps} />
                    </div>
                    {/* <!-- paging component --> */}
                  </>
                ) : (
                  <>
                    <p className="text-xl font-semibold pb-2">
                      {t("CustomerPolicies-no-policy-title")}
                    </p>
                    <p className="text-sm">
                      {t("CustomerPolicies-no-policy-subtitle")}
                    </p>
                  </>
                )}
              </div>
              <p className="block mt-6 text-sm md:hidden">
                <Trans
                  i18nKey="CustomerDashboard-policySubTitle"
                  values={{ date: moment().format("MM/DD/YYYY") }}
                />
              </p>
            </div>
            <div className="container">
              <div className="flex flex-col lg:grid lg:gap-x-4 lg:grid-cols-3 -mb-8">
                {/* <!-- Things You Can Do --> */}
                {viewThingsYouCanDo && (
                  <div>
                    <>
                      <h2>{t("CustomerDashboard-sectionTwoTitle")}</h2>
                      <p className="text-sm">
                        {t("CustomerDashboard-sectionTwoSubTitle")}
                      </p>
                      <hr />
                    </>
                    <HelpfulLinks links={helpfulLinks} />
                  </div>
                )}
                {/* <!-- Things You Can Do --> */}

                {/* <!-- My Requests & Claims --> */}
                <>
                  {!showMyPolicies && viewServiceandClaim && (
                    <div className="lg:col-span-2">
                      <div
                        className="lg:col-span-2"
                        role="tablist"
                        aria-label="My Requests &amp; Claims"
                      >
                        <h2>{t("CustomerDashboard-sectionThreeTitle")}</h2>
                        <p className="text-sm">
                          {t("CustomerDashboard-sectionThreeSubTitle")}
                        </p>
                        <hr />
                      </div>

                      <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-x-4">
                        <div className="ui-card ui-component-spacing md:flex md:flex-col md:justify-between">
                          <div className="font-semibold p-4 pb-2 hidden md:block">
                            {t("CustomerDashboard-sectionThreeTitle2")}
                          </div>
                          {claims.length > 0 &&
                            claims
                              .slice(0, 2)
                              .map((claim, idx) => (
                                <SimpleDataRow
                                  type="customerServicesAndClaims"
                                  key={`Customer claim - ${idx}`}
                                  data={claim}
                                />
                              ))}
                          <Link
                            to="/services-claims/claims"
                            className="text-center ui-card-btn-more"
                            aria-label="View more service requests"
                          >
                            {t("CustomerDashboard-sectionTwoView")}
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </>

                <Tab.Group className="md:hidden" as="div">
                  <div className="ui-card flex-grow px-2 p-1 mb-4">
                    <Tab.List>
                      <Tab className="text-sm mx-3 pt-3 pb-2 mb-1">
                        {t("CustomerDashboard-sectionTwoTitle")}
                      </Tab>
                    </Tab.List>
                  </div>
                  <Tab.Panels>
                    <Tab.Panel>
                      <div className="ui-card ui-component-spacing">
                        {claims.length > 0 &&
                          claims.map((claim, idx) => (
                            <SimpleDataRow
                              type="customerServicesAndClaims"
                              key={`Customer claim - ${idx}`}
                              data={claim}
                            />
                          ))}
                        <button className="ui-card-btn-more">
                          {t("CustomerDashboard-sectionTwoView")}
                        </button>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
                {/* <!-- My Requests & Claims --> */}
              </div>
              <hr></hr>
              <div className="text-center text-xs text-theme-text-secondary px-4">
                {t("CustomerDashboard-infoDescription")}
              </div>
            </div>
          </LoadingOverlay>
        </>
      )}
    </>
  );
}
